import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Button, Box, Typography, Paper, IconButton, Alert, CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

import axios from 'axios';
//import { IFormatCompte, IDetailFormatCompte } from '../types/IFormatCompte'; // Assurez-vous que le chemin est correct
//import { IFormatCompte, IDetailFormatCompte } from '../interfaces/GlobalInterface'; // Assurez-vous que le chemin est correct
import { IFormatCompte,IDetailFormatCompte } from '../../../dataTypes/GlobalDataType';
import { apiEndpoints } from '../../../config';
// URL de votre API (à adapter)
const API_BASE_URL = 'https://csal.f2itech.tech:450/api/FormatCompte';

interface FormatdetailsEditorProps {
  formatId: number;
}

const FormatCompte3: React.FC<FormatdetailsEditorProps> = ({ formatId }) => {
  const [currentFormat, setCurrentFormat] = useState<IFormatCompte | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // --- Chargement des données du format au montage du composant ---
  useEffect(() => {
    const fetchFormatdetails = async () => {
      setLoading(true);
      setError(null);
      try {

        const response = await axios.get<IFormatCompte>(`${apiEndpoints.formatcompte}/${formatId}`);
        //console.log("details: 1111", response.data);
        // Assurez-vous que les détails sont triés initialement
        const sorteddetails = response.data.details
          ? [...response.data.details].sort((a, b) => (a.ordreAffichage || 0) - (b.ordreAffichage || 0))
          : [];
        setCurrentFormat({ ...response.data, details: sorteddetails });
      } catch (err) {
        console.error("Failed to fetch format details:", err);
        setError("Erreur lors du chargement des détails du format.");
      } finally {
        setLoading(false);
      }
    };

    fetchFormatdetails();
  }, [formatId]); // Recharger si formatId change

//   // Liste des types de segments disponibles (à adapter)
//   const segmentTypes = [
//     'PlanComptable',
//     'CodeProduit',
//     'Codage',
//     'NumeroClient',
//     'ClefDeCompte',
//     'NumeroOrdre', // Nouveau type
//     'TexteLibre', // Exemple de type supplémentaire
//     // Ajoutez d'autres types si nécessaire
//   ];

  // Liste complète des noms de groupe possibles (pour la colonne "Nom du Groupe")
  const groupNames = [
    'PlanComptable',
    'CodeProduit',
    'Codage',
    'NumeroClient',
    'ClefDeCompte',
    'NumeroOrdre', // AJOUT IMPORTANT
    'TexteLibre', // Si vous avez un segment pour du texte libre (qui n'est pas un paramètre d'entrée)
    // ... ajoutez d'autres noms de groupe si nécessaire
  ];

  // Liste des types de segments disponibles (pour la colonne "Type de Segment")
  const segmentTypes = [
    'Numerique', // Pour les champs qui doivent être paddés avec des zéros
    'Alphanumerique', // Pour les champs qui peuvent avoir des lettres/chiffres, paddés avec des espaces
    'Compteur', // Spécifique pour les compteurs comme NumeroOrdre
    'Fixe', // Pour du texte fixe ou des symboles
    // ... ajoutez d'autres types si nécessaire
  ];

  // --- Gestion du changement d'entrée pour un détail spécifique ---
  const handleDetailInputChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setSaveSuccess(false); // Réinitialiser le succès à chaque modification

    setCurrentFormat(prevFormat => {
      if (!prevFormat || !prevFormat.details) return prevFormat;

      const updateddetails = prevFormat.details.map((detail, i) => {
        if (i === index) {
          return {
            ...detail,
            // Convertir les valeurs numériques si nécessaire
            [name]: (name === 'ordreAffichage' || name === 'longueur' || name === 'positionDepart')
              ? parseInt(value, 10) || 0 // ParseInt et gérer NaN pour vide/non-numérique
              : value,
          };
        }
        return detail;
      });

      // Si l'ordreAffichage est modifié, resynchroniser les ordres d'affichage pour éviter les doublons
      // et trier à nouveau pour la cohérence de l'interface utilisateur.
      if (name === 'ordreAffichage') {
        const newOrder = parseInt(value, 10);
        if (!isNaN(newOrder) && newOrder > 0) {
            // Créer une copie pour ne pas muter directement l'objet
            let detailsToReorder = [...updateddetails];

            // Retirer l'élément modifié pour le réinsérer à la bonne place
            const [movedDetail] = detailsToReorder.splice(index, 1);
            movedDetail.ordreAffichage = newOrder;

            // Insérer l'élément à sa nouvelle position (basé sur le nouvel ordre)
            // Trouver l'index où insérer pour maintenir le tri
            let insertIndex = 0;
            while (insertIndex < detailsToReorder.length && detailsToReorder[insertIndex].ordreAffichage < newOrder) {
                insertIndex++;
            }
            detailsToReorder.splice(insertIndex, 0, movedDetail);

            // Resynchroniser tous les ordreAffichage pour qu'ils soient séquentiels
            // après le déplacement ou la modification manuelle.
            const finalSorteddetails = detailsToReorder.map((d, i) => ({
                ...d,
                ordreAffichage: i + 1 // Assurez des ordres séquentiels
            }));
            return { ...prevFormat, details: finalSorteddetails };

        }
      }

      return { ...prevFormat, details: updateddetails };
    });
  };

  // --- Gestion du déplacement d'un détail (boutons flèche) ---
  const handleMoveDetail = (index: number, direction: 'up' | 'down') => {
    setSaveSuccess(false);
    setCurrentFormat(prevFormat => {
      if (!prevFormat || !prevFormat.details) return prevFormat;

      const details = [...prevFormat.details];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;

      if (targetIndex >= 0 && targetIndex < details.length) {
        // Échanger les éléments
        [details[index], details[targetIndex]] = [details[targetIndex], details[index]];

        // Resynchroniser l'ordreAffichage après l'échange
        const reordereddetails = details.map((d, i) => ({
          ...d,
          ordreAffichage: i + 1 // Assign new sequential orders
        }));
        return { ...prevFormat, details: reordereddetails };
      }
      return prevFormat;
    });
  };

  // --- Gestion de la suppression d'un détail ---
  const handleDeleteDetail = (indexToDelete: number) => {
    setSaveSuccess(false);
    setCurrentFormat(prevFormat => {
      if (!prevFormat || !prevFormat.details) return prevFormat;

      const updateddetails = prevFormat.details.filter((_, i) => i !== indexToDelete);

      // Resynchroniser l'ordreAffichage après suppression
      const reordereddetails = updateddetails.map((d, i) => ({
        ...d,
        ordreAffichage: i + 1 // Maintain sequential orders
      }));

      return { ...prevFormat, details: reordereddetails };
    });
  };

  // --- Gestion de l'ajout d'un nouveau détail ---
  const handleAddDetail = () => {
    setSaveSuccess(false);
    setCurrentFormat(prevFormat => {
      if (!prevFormat) return prevFormat;

      const newDetail: IDetailFormatCompte = {
        idFormat: prevFormat.idFormat,
        ordreAffichage: (prevFormat.details?.length || 0) + 1,
        nomGroupe: '', // Laisser vide ou proposer un défaut pertinent, ex: 'TexteLibre' ou le premier de la liste
        typeSegment: 'Alphanumerique', // Type par défaut sensé
        longueur: 0,
        positionDepart: 0,
        separateurAvant: '',
      };

      return {
        ...prevFormat,
        details: [...(prevFormat.details || []), newDetail],
      };
    });
  };

  // --- Gestion de la soumission du formulaire (sauvegarde) ---
  const handleSavedetails = async () => {
    if (!currentFormat) return;

    // Validation basique avant l'envoi
    const isValid = currentFormat.details?.every(detail =>
        detail.nomGroupe &&
        detail.longueur > 0 &&
        detail.positionDepart > 0 &&
        detail.ordreAffichage > 0 
       // detail.typeSegment 
        //detail.separateurAvant !== undefined // Vérifier si le séparateur avant est défini
        // Assurez-vous que le type de segment est défini
    );

    // if (!isValid) {
    //     setError("Veuillez remplir tous les champs obligatoires et s'assurer que les valeurs numériques sont positives.");
    //     return;
    // }

    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Envoyer le format complet, y compris les détails mis à jour, à votre API
      // Assurez-vous que votre API peut gérer la mise à jour des détails (création/suppression/modification)
      // C'est un appel PUT/PATCH vers l'endpoint du format
      await axios.put(`${apiEndpoints.formatcompte}/${currentFormat.idFormat}`, currentFormat);
      setSaveSuccess(true);
      // Recharger les détails du serveur pour s'assurer de la cohérence si l'API a une logique spéciale de persistance
      // Ou simplement réinitialiser l'état local si l'API renvoie les données à jour
      // Pour cet exemple, nous allons recharger
      // await fetchFormatdetails(); // Décommenter si vous voulez un rechargement complet
    } catch (err) {
      console.error("Failed to save format details:", err);
      setError("Erreur lors de la sauvegarde des détails du format.");
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !currentFormat) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          Réessayer
        </Button>
      </Box>
    );
  }

  if (!currentFormat) {
    return <Typography>Aucun format trouvé pour l'ID {formatId}.</Typography>;
  }

  return (
    <Box sx={{ p: 1, maxWidth: '1000px', margin: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Modifier le Format: {currentFormat.nomFormat} (ID: {currentFormat.idFormat})
      </Typography>

      <Box sx={{ mb: 1 }}>
        <TextField
          label="Nom du Format"
          value={currentFormat.nomFormat}
          onChange={(e) => setCurrentFormat({ ...currentFormat, nomFormat: e.target.value })}
          fullWidth
          margin="normal"
          size="small"
        />
        <TextField
          label="Séparateur"
          value={currentFormat.separateur}
          onChange={(e) => setCurrentFormat({ ...currentFormat, separateur: e.target.value })}
          fullWidth
          margin="normal"
          size="small"
        />
      </Box>

      <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 1 }}>
        Détails du Groupe de Compte
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '100px',backgroundColor: "#116599", color: '#fff' }}>Ordre</TableCell>
              <TableCell sx={{ backgroundColor: "#116599", color: '#fff' }}>Nom du Groupe</TableCell>
               <TableCell sx={{ backgroundColor: "#116599", color: '#fff' }}>Type de Segment</TableCell> {/* Nouvelle colonne */}
              <TableCell sx={{ backgroundColor: "#116599", color: '#fff' }}>longueur</TableCell>
              <TableCell sx={{ backgroundColor: "#116599", color: '#fff' }}>Position de Départ</TableCell>
              <TableCell sx={{ backgroundColor: "#116599", color: '#fff' }}>Séparateur Avant</TableCell> {/* Nouvelle colonne */}
              <TableCell sx={{ backgroundColor: "#116599", color: '#fff' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentFormat.details && currentFormat.details.length > 0 ? (
              currentFormat.details
                .map((detail, index) => (
                  <TableRow key={detail.idDetail || `new-${index}`}>
                    <TableCell sx={{ width: '80px' }}>
                      <TextField
                        type="number"
                        name="ordreAffichage"
                        value={detail.ordreAffichage || ''}
                        onChange={(e) => handleDetailInputChange(index, e)}
                        inputProps={{ min: 1 }}
                        size="small"
                        sx={{ width: '60px', mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <IconButton size="small" onClick={() => handleMoveDetail(index, 'up')} disabled={index === 0}>
                          <ArrowUpwardIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleMoveDetail(index, 'down')} disabled={index === (currentFormat.details?.length || 0) - 1}>
                          <ArrowDownwardIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </TableCell>

 
              {/* Cellule Nom du Groupe (Maintenant un Select) */}
              <TableCell>
                <FormControl fullWidth size="small">
                  <InputLabel>Nom Groupe</InputLabel>
                  <Select
                    name="nomGroupe"
                    value={detail.nomGroupe}
                    label="Nom Groupe"
                    onChange={(e) => handleDetailInputChange(index, e as React.ChangeEvent<HTMLInputElement>)}
                    required
                  >
                    {groupNames.map(name => (
                      <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
                    {/* <TableCell>
                      <TextField
                        name="nomGroupe"
                        value={detail.nomGroupe}
                        onChange={(e) => handleDetailInputChange(index, e)}
                        required
                        size="small"
                        fullWidth
                      />
                    </TableCell> */}

                     {/* Nouveau champ: Type de Segment */}
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>Type</InputLabel>
                        <Select
                          name="typeSegment"
                          value={detail.typeSegment}
                          label="Type"
                          onChange={(e) => handleDetailInputChange(index, e as React.ChangeEvent<HTMLInputElement>)}
                          required
                        >
                          {segmentTypes.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    
                    <TableCell>
                      <TextField
                        type="number"
                        name="longueur"
                        //value={detail.longueur || ''}
                        onChange={(e) => handleDetailInputChange(index, e)}
                        inputProps={{ min: 1 }}
                        required
                        size="small"
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        name="positionDepart"
                        //value={detail.positionDepart || ''}
                        onChange={(e) => handleDetailInputChange(index, e)}
                        inputProps={{ min: 1 }}
                        required
                        size="small"
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                     {/* Nouveau champ: Séparateur Avant */}
                    <TableCell>
                      <TextField
                        name="separateurAvant"
                        value={detail.separateurAvant || ''}
                        onChange={(e) => handleDetailInputChange(index, e)}
                        size="small"
                        sx={{ width: '60px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="error" onClick={() => handleDeleteDetail(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Aucun détail de format défini.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDetail}
        >
          Ajouter Détail
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSavedetails}
          disabled={saving}
        >
          {saving ? <CircularProgress size={24} /> : "Enregistrer"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mt: 3 }}>Modifications sauvegardées avec succès !</Alert>}
    </Box>
  );
};

export default FormatCompte3;